import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

import { getFirmwareUpgradeBatch } from '@sophocles/api/firmwareUpgrades';
import { SOCKET_IO_URL } from '@sophocles/config';

const POLL_INTERVAL_MS = 5000;

/**
 * Subscribe to per-batch firmware upgrade progress.
 *
 * Connects to the /upgrades socket.io namespace and joins room `batch:{id}`.
 * When the websocket is not connected, falls back to polling
 * GET /firmware-upgrades/{id}/ at a 5s cadence — the response shape includes
 * `units` rows that the caller already renders.
 *
 * Returns ``{ batch, error, connected, refetch, lastEventAt }``. Caller
 * supplies the initial batch from its own fetch; this hook keeps it fresh.
 */
export default function useUpgradeBatchSocket(batchId) {
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [lastEventAt, setLastEventAt] = useState(null);
  const socketRef = useRef(null);
  const pollTimerRef = useRef(null);

  const fetchOnce = async () => {
    try {
      const response = await getFirmwareUpgradeBatch(batchId);
      setBatch(response.data);
      setError(null);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    if (!batchId) return undefined;

    let cancelled = false;

    const ensurePolling = () => {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(() => {
        if (!cancelled) fetchOnce();
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    fetchOnce();

    const socket = io(`${SOCKET_IO_URL}/upgrades`, { transports: ['websocket'] });
    socketRef.current = socket;

    const room = `batch:${batchId}`;

    socket.on('connect', () => {
      if (cancelled) return;
      setConnected(true);
      stopPolling();
      socket.emit('join', room);
    });

    socket.on('disconnect', () => {
      if (cancelled) return;
      setConnected(false);
      ensurePolling();
    });

    socket.on('connect_error', () => {
      if (cancelled) return;
      setConnected(false);
      ensurePolling();
    });

    socket.on('notify', payload => {
      if (cancelled) return;
      setLastEventAt(new Date());
      // Re-fetch the batch detail to pick up any DB-side mutation
      // (the socket payload is informational; the polling endpoint is the
      // source of truth for the rendered grid).
      fetchOnce();
    });

    // Belt-and-braces: poll on mount until the socket connects
    ensurePolling();

    return () => {
      cancelled = true;
      stopPolling();
      try {
        socket.emit('leave', room);
      } catch (_) {
        // ignore — socket may already be disconnected
      }
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('notify');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [batchId]);

  return { batch, error, connected, refetch: fetchOnce, lastEventAt };
}
